import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDeShort } from "@/lib/date";
import { toast } from "sonner";
import GoogleClassroomConnect from "@/components/GoogleClassroomConnect";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, GraduationCap, Users, Trash2, Pencil, Sparkles, ChevronDown, ChevronUp, X as XIcon, ListTree, BookMarked, UserCircle } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import GermanDateInput from "@/components/GermanDateInput";
import WeiterbildungMassnahmenKatalog from "@/components/WeiterbildungMassnahmenKatalog";
import WeiterbildungKundenListe from "@/components/WeiterbildungKundenListe";

export default function WeiterbildungPage() {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "manager";
  const isAdmin = user?.role === "admin";
  const [classes, setClasses] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mainTab, setMainTab] = useState("klassen");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, coRes] = await Promise.all([
        api.get("/weiterbildung/classes"),
        canManage ? api.get("/coaches") : Promise.resolve({ data: [] }),
      ]);
      setClasses(cRes.data);
      // Only show `lecturer` users in the Dozenten-Multi-Select (that's what the section is for).
      setCoaches((coRes.data || []).filter((c) => c.role === "lecturer"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (c) => {
    if (!window.confirm(`Klasse "${c.name}" löschen? Alle Anwesenheitsdaten gehen verloren.`)) return;
    try {
      await api.delete(`/weiterbildung/classes/${c.class_id}`);
      toast.success("Klasse gelöscht");
      fetchAll();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="weiterbildung-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500 mb-2">Weiterbildung</div>
          <h1 className="font-heading text-5xl font-light tracking-tighter text-slate-900">{mainTab === "katalog" ? "Maßnahmen-Katalog" : mainTab === "kunden" ? "Kunden" : "Klassen"}</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-xl leading-relaxed">
            {mainTab === "katalog"
              ? "Zertifizierte Weiterbildungsmaßnahmen mit fest vorgegebener UE-Zahl und Preis/UE — Grundlage für die Kundenakte und den Teilnahmevertrag."
              : mainTab === "kunden"
              ? "Weiterbildungskunden erfassen (wie bei AVGS) und anschließend einer Klasse zuweisen."
              : "Verwaltung von Weiterbildungs-Klassen, Teilnehmern, Dozenten, digitalem Klassenbuch und Anwesenheitslisten."}
          </p>
        </div>
        {canManage && mainTab === "klassen" && (
          <button
            data-testid="add-class-btn"
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="bg-slate-900 text-white px-6 py-3 text-sm font-medium hover:bg-slate-800 flex items-center gap-2"
          >
            <Plus size={16} strokeWidth={1.5} /> Klasse anlegen
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-slate-200 mb-6">
        <button onClick={() => setMainTab("klassen")} data-testid="wb-main-tab-klassen"
          className={`px-4 py-2.5 text-xs font-bold uppercase tracking-overline flex items-center gap-1.5 ${mainTab === "klassen" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
          <ListTree size={12} strokeWidth={1.5} /> Klassen
        </button>
        {canManage && (
          <button onClick={() => setMainTab("kunden")} data-testid="wb-main-tab-kunden"
            className={`px-4 py-2.5 text-xs font-bold uppercase tracking-overline flex items-center gap-1.5 ${mainTab === "kunden" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            <UserCircle size={12} strokeWidth={1.5} /> Kunden
          </button>
        )}
        {canManage && (
          <button onClick={() => setMainTab("katalog")} data-testid="wb-main-tab-katalog"
            className={`px-4 py-2.5 text-xs font-bold uppercase tracking-overline flex items-center gap-1.5 ${mainTab === "katalog" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
            <BookMarked size={12} strokeWidth={1.5} /> Maßnahmen-Katalog
          </button>
        )}
      </div>

      {mainTab === "katalog" ? (
        <WeiterbildungMassnahmenKatalog />
      ) : mainTab === "kunden" ? (
        <WeiterbildungKundenListe />
      ) : (
        <>
          {loading && <div className="text-xs font-bold uppercase text-slate-500">Lädt…</div>}

          {canManage && !loading && (
            <div className="mb-6">
              <GoogleClassroomConnect />
            </div>
          )}

          {!loading && classes.length === 0 && (
            <div className="border border-dashed border-slate-300 p-16 text-center">
              <GraduationCap size={48} strokeWidth={1} className="mx-auto text-slate-300 mb-4" />
              <div className="text-slate-500">Noch keine Klassen angelegt.</div>
            </div>
          )}

          <div className="grid gap-4">
            {classes.map((c) => (
              <div key={c.class_id} className="border border-slate-200 p-6 flex items-start justify-between gap-6 hover:bg-slate-50 transition-colors" data-testid={`class-card-${c.class_id}`}>
                <Link to={`/weiterbildung/klasse/${c.class_id}`} className="flex-1 min-w-0">
                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1">{c.measure_number} · {c.weiterbildung_name}</div>
                  <div className="font-heading text-2xl font-light tracking-tighter text-slate-900 mb-2">{c.name}</div>
                  <div className="text-xs text-slate-600 font-mono flex items-center gap-3 flex-wrap">
                    <span>{formatDeShort(c.start_date)} – {formatDeShort(c.end_date)}</span>
                    <span className="flex items-center gap-1"><Users size={11} strokeWidth={1.5} /> {c.participant_count} Teilnehmer</span>
                    <span>{(c.lecturer_ids || []).length} Dozent{(c.lecturer_ids || []).length === 1 ? "" : "en"}</span>
                  </div>
                </Link>
                {canManage && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setEditing(c); setDialogOpen(true); }}
                      className="px-3 py-2 border border-slate-200 text-xs text-slate-700 hover:bg-slate-100 flex items-center gap-1"
                    >
                      <Pencil size={11} strokeWidth={1.5} /> Bearbeiten
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => remove(c)}
                        className="px-3 py-2 border border-slate-200 text-xs text-red-600 hover:bg-red-50 flex items-center gap-1"
                      >
                        <Trash2 size={11} strokeWidth={1.5} /> Löschen
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      <ClassDialog
        open={dialogOpen}
        onOpenChange={(o) => { setDialogOpen(o); if (!o) setEditing(null); }}
        initialData={editing}
        allCoaches={coaches}
        onSaved={fetchAll}
      />
    </div>
  );
}

function ClassDialog({ open, onOpenChange, initialData, allCoaches, onSaved }) {
  const editing = !!initialData;
  const [form, setForm] = useState({
    name: "", weiterbildung_name: "", measure_number: "",
    start_date: "", end_date: "", lecturer_ids: [],
    default_time_from: "09:00", default_time_to: "13:00",
    google_classroom_url: "", classroom_course_id: "",
    concept_raw: "", concept_description: "", concept_goals: [], total_ue: null, curriculum: [], weekly_plan: [],
  });
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [conceptOpen, setConceptOpen] = useState(false);
  const [googleCourses, setGoogleCourses] = useState(null); // null = not loaded, [] = empty
  const [googleConnected, setGoogleConnected] = useState(false);

  useEffect(() => {
    if (initialData) {
      setForm({
        name: initialData.name || "",
        weiterbildung_name: initialData.weiterbildung_name || "",
        measure_number: initialData.measure_number || "",
        start_date: initialData.start_date || "",
        end_date: initialData.end_date || "",
        lecturer_ids: initialData.lecturer_ids || [],
        default_time_from: initialData.default_time_from || "09:00",
        default_time_to: initialData.default_time_to || "13:00",
        google_classroom_url: initialData.google_classroom_url || "",
        classroom_course_id: initialData.classroom_course_id || "",
        concept_raw: initialData.concept_raw || "",
        concept_description: initialData.concept_description || "",
        concept_goals: initialData.concept_goals || [],
        total_ue: initialData.total_ue ?? null,
        curriculum: initialData.curriculum || [],
        weekly_plan: initialData.weekly_plan || [],
      });
      setConceptOpen(!!(initialData.concept_description || (initialData.curriculum || []).length));
    } else if (open) {
      setForm({ name: "", weiterbildung_name: "", measure_number: "", start_date: "", end_date: "", lecturer_ids: [], default_time_from: "09:00", default_time_to: "13:00", google_classroom_url: "", classroom_course_id: "", concept_raw: "", concept_description: "", concept_goals: [], total_ue: null, curriculum: [], weekly_plan: [] });
      setConceptOpen(false);
    }
  }, [initialData, open]);

  // Load Google connection + course list when dialog opens
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const s = await api.get("/google/status");
        setGoogleConnected(!!s.data?.connected);
        if (s.data?.connected) {
          const c = await api.get("/google/courses");
          setGoogleCourses(c.data || []);
        } else {
          setGoogleCourses(null);
        }
      } catch {
        setGoogleCourses(null);
      }
    })();
  }, [open]);

  const pickGoogleCourse = (courseId) => {
    if (!courseId) {
      setForm((f) => ({ ...f, classroom_course_id: "", google_classroom_url: "" }));
      return;
    }
    const c = (googleCourses || []).find((x) => x.id === courseId);
    setForm((f) => ({
      ...f,
      classroom_course_id: courseId,
      google_classroom_url: c?.alternate_link || f.google_classroom_url,
    }));
  };

  const toggleLecturer = (uid) => setForm((f) => ({ ...f, lecturer_ids: f.lecturer_ids.includes(uid) ? f.lecturer_ids.filter((x) => x !== uid) : [...f.lecturer_ids, uid] }));

  const parseConcept = async () => {
    if (!form.concept_raw || form.concept_raw.trim().length < 20) {
      toast.error("Bitte zuerst den Konzept-Text einfügen (mind. 20 Zeichen).");
      return;
    }
    setParsing(true);
    try {
      const { data } = await api.post("/weiterbildung/concept/parse", { raw_text: form.concept_raw });
      setForm((f) => ({
        ...f,
        concept_description: data.description || f.concept_description,
        concept_goals: data.goals || [],
        total_ue: data.total_ue ?? f.total_ue,
        curriculum: data.curriculum || [],
      }));
      toast.success(`Curriculum extrahiert: ${data.curriculum?.length || 0} Themenblöcke, ${data.goals?.length || 0} Ziele.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setParsing(false);
    }
  };

  const [planning, setPlanning] = useState(false);
  const planWeeks = async () => {
    if (!initialData?.class_id) {
      toast.error("Bitte erst die Klasse speichern, dann Wochen-Plan generieren.");
      return;
    }
    if (!form.curriculum?.length) {
      toast.error("Bitte zuerst das Curriculum via KI-Parse anlegen.");
      return;
    }
    setPlanning(true);
    try {
      const { data } = await api.post(`/weiterbildung/classes/${initialData.class_id}/plan-weeks`);
      const weeks = data.weeks || [];
      setForm((f) => ({ ...f, weekly_plan: weeks }));
      // Persist immediately so the Klassenbuch chip suggestions work even before user saves the dialog
      try { await api.put(`/weiterbildung/classes/${initialData.class_id}`, { weekly_plan: weeks }); } catch { /* silent */ }
      toast.success(`Wochen-Plan generiert: ${weeks.length} Wochen.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPlanning(false);
    }
  };

  const submit = async () => {
    if (!form.name || !form.weiterbildung_name || !form.measure_number || !form.start_date || !form.end_date) {
      toast.error("Bitte alle Pflichtfelder ausfüllen"); return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/weiterbildung/classes/${initialData.class_id}`, form);
        toast.success("Klasse aktualisiert");
      } else {
        await api.post("/weiterbildung/classes", form);
        toast.success("Klasse angelegt");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl rounded-none border border-slate-200 p-0" data-testid="class-dialog">
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-slate-100">
          <DialogTitle className="font-heading text-3xl font-light tracking-tighter">{editing ? "Klasse bearbeiten" : "Neue Klasse"}</DialogTitle>
        </DialogHeader>
        <div className="px-8 py-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Klassenname *</label>
            <input data-testid="class-name-input" className="input-tech" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Weiterbildung *</label>
              <input data-testid="class-wb-input" className="input-tech" value={form.weiterbildung_name} onChange={(e) => setForm({ ...form, weiterbildung_name: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Maßnahmennummer *</label>
              <input data-testid="class-measure-input" className="input-tech font-mono" value={form.measure_number} onChange={(e) => setForm({ ...form, measure_number: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Beginn *</label>
              <GermanDateInput className="input-tech" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Ende *</label>
              <GermanDateInput className="input-tech" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Standard-Beginn (Uhrzeit)</label>
              <input type="time" className="input-tech" value={form.default_time_from} onChange={(e) => setForm({ ...form, default_time_from: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Standard-Ende (Uhrzeit)</label>
              <input type="time" className="input-tech" value={form.default_time_to} onChange={(e) => setForm({ ...form, default_time_to: e.target.value })} />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-bold uppercase tracking-overline text-slate-500">Dozenten zuweisen</label>
              {form.lecturer_ids.length > 0 && (
                <span className="text-[10px] text-slate-500 font-mono">{form.lecturer_ids.length} ausgewählt</span>
              )}
            </div>
            {(allCoaches || []).length === 0 ? (
              <div className="border border-dashed border-slate-300 p-4 text-xs text-slate-500 italic">
                Keine Dozenten vorhanden. Bitte erst unter <b>Dozenten</b> in der Sidebar eine/n Dozent/-in anlegen.
              </div>
            ) : (
              <div className="border border-slate-200 max-h-52 overflow-y-auto divide-y divide-slate-100" data-testid="lecturer-multi-select">
                {(allCoaches || []).map((c) => {
                  const checked = form.lecturer_ids.includes(c.user_id);
                  return (
                    <label
                      key={c.user_id}
                      className={`flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer text-sm ${checked ? "bg-[#e8f6f9]" : ""}`}
                      data-testid={`lecturer-opt-${c.user_id}`}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggleLecturer(c.user_id)} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">{c.name}</div>
                        <div className="text-[10px] text-slate-500 font-mono truncate">{c.email}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">
              Google Classroom {googleConnected ? "(aus verbundenem Konto wählen)" : "Link (optional)"}
            </label>
            {googleConnected && googleCourses !== null ? (
              <div className="space-y-2">
                {googleCourses.length === 0 ? (
                  <div className="border border-dashed border-slate-300 p-3 text-xs text-slate-500 italic">
                    Keine aktiven Google Classrooms unter dem verbundenen Konto gefunden. Bitte prüfe deine Lehrer-Zuweisungen in Google Classroom.
                  </div>
                ) : (
                  <select
                    className="input-tech font-mono"
                    value={form.classroom_course_id || ""}
                    onChange={(e) => pickGoogleCourse(e.target.value)}
                    data-testid="classroom-course-select"
                  >
                    <option value="">— Kein Classroom zugewiesen —</option>
                    {googleCourses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.section ? ` · ${c.section}` : ""}
                      </option>
                    ))}
                  </select>
                )}
                {form.google_classroom_url && (
                  <div className="text-[10px] text-slate-500 font-mono truncate">
                    ↳ {form.google_classroom_url}
                  </div>
                )}
              </div>
            ) : (
              <input
                type="url"
                placeholder="https://classroom.google.com/c/…"
                className="input-tech font-mono"
                value={form.google_classroom_url}
                onChange={(e) => setForm({ ...form, google_classroom_url: e.target.value, classroom_course_id: "" })}
                data-testid="class-classroom-url-input"
              />
            )}
            <div className="text-[10px] text-slate-500 mt-1">
              {googleConnected
                ? "Wähle den Classroom aus. Die Kurs-ID wird automatisch gespeichert und offene Aufgaben erscheinen im Klassen-Dashboard + Teilnehmer-Portal."
                : "Ohne Google-Verbindung wird der Link nur als Direktlink angezeigt. Verbinde Google Classroom auf /weiterbildung um Aufgaben live zu sehen."}
            </div>
          </div>

          {/* ---------------- Konzept & Curriculum ---------------- */}
          <div className="border border-slate-200">
            <button
              type="button"
              onClick={() => setConceptOpen(!conceptOpen)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50"
              data-testid="concept-toggle"
            >
              <div className="flex items-center gap-2">
                <Sparkles size={14} strokeWidth={1.5} className="text-[#0a788c]" />
                <span className="text-xs font-bold uppercase tracking-overline text-slate-700">
                  Konzept & Curriculum{form.curriculum.length > 0 ? ` · ${form.curriculum.length} Themenblöcke` : ""}
                </span>
              </div>
              {conceptOpen ? <ChevronUp size={16} strokeWidth={1.5} /> : <ChevronDown size={16} strokeWidth={1.5} />}
            </button>
            {conceptOpen && (
              <div className="px-4 pb-4 space-y-4 border-t border-slate-100 pt-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">
                    Roh-Konzept (aus Antragsunterlagen einfügen)
                  </label>
                  <textarea
                    rows={8}
                    className="input-tech resize-y text-xs font-mono"
                    placeholder="Kopiere hier Inhalte, Kompetenzen und zeitliche Gliederung deiner Weiterbildung rein — die KI extrahiert daraus automatisch Themenblöcke, UE-Zahlen und Lernziele."
                    value={form.concept_raw}
                    onChange={(e) => setForm({ ...form, concept_raw: e.target.value })}
                    data-testid="concept-raw-textarea"
                  />
                  <button
                    type="button"
                    onClick={parseConcept}
                    disabled={parsing || !form.concept_raw}
                    className="mt-2 px-4 py-2 bg-[#0a788c] text-white text-xs font-medium hover:bg-[#0a96b4] disabled:opacity-50 flex items-center gap-2"
                    data-testid="concept-parse-btn"
                  >
                    <Sparkles size={12} strokeWidth={1.5} />
                    {parsing ? "KI analysiert…" : "Struktur automatisch erkennen"}
                  </button>
                </div>

                {(form.concept_description || form.total_ue || form.concept_goals.length > 0 || form.curriculum.length > 0) && (
                  <div className="space-y-3 border-t border-slate-100 pt-4" data-testid="concept-parsed">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Kurzbeschreibung</label>
                      <textarea rows={2} className="input-tech text-xs" value={form.concept_description}
                        onChange={(e) => setForm({ ...form, concept_description: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Gesamt-UE</label>
                        <input type="number" className="input-tech font-mono" value={form.total_ue ?? ""} onChange={(e) => setForm({ ...form, total_ue: e.target.value ? parseInt(e.target.value, 10) : null })} />
                      </div>
                      <div>
                        <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Themenblöcke</label>
                        <div className="text-sm font-mono">
                          {form.curriculum.length} Block{form.curriculum.length === 1 ? "" : "s"} · {form.curriculum.reduce((s, c) => s + (c.ue || 0), 0) || 0} UE zugeordnet
                        </div>
                      </div>
                    </div>
                    {form.concept_goals.length > 0 && (
                      <div>
                        <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Lernziele / Kompetenzen</label>
                        <ul className="space-y-1">
                          {form.concept_goals.map((g, i) => (
                            <li key={i} className="flex items-center gap-2 group">
                              <span className="text-[10px] text-slate-400 font-mono w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                              <input
                                value={g}
                                onChange={(e) => setForm({ ...form, concept_goals: form.concept_goals.map((x, j) => (j === i ? e.target.value : x)) })}
                                className="input-tech text-xs flex-1"
                              />
                              <button
                                type="button"
                                onClick={() => setForm({ ...form, concept_goals: form.concept_goals.filter((_, j) => j !== i) })}
                                className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-red-600"
                                title="Entfernen"
                              >
                                <XIcon size={12} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {form.curriculum.length > 0 && (
                      <div>
                        <label className="text-xs font-bold uppercase tracking-overline text-slate-500 block mb-2">Curriculum</label>
                        <div className="border border-slate-200 divide-y divide-slate-100 max-h-64 overflow-y-auto">
                          {form.curriculum.map((c, i) => (
                            <div key={i} className="p-3 hover:bg-slate-50">
                              <div className="flex items-baseline justify-between gap-3 mb-1">
                                <div className="text-sm font-medium flex-1">{c.topic}</div>
                                <div className="text-xs font-mono text-slate-500">{c.ue ? `${c.ue} UE` : "—"}</div>
                              </div>
                              {(c.subtopics || []).length > 0 && (
                                <div className="text-[11px] text-slate-500 pl-1">
                                  {(c.subtopics || []).join(" · ")}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="text-[10px] text-slate-500 italic bg-emerald-50 border border-emerald-200 p-2">
                      Diese Konzept-Daten nutzt die KI später als Grundlage für Klassenbuch-Vorschläge und Dokumentation.
                    </div>

                    {/* Weekly plan */}
                    <div className="border-t border-slate-100 pt-4">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-bold uppercase tracking-overline text-slate-500">
                          Wochen-Plan{form.weekly_plan.length ? ` · ${form.weekly_plan.length} Wochen` : ""}
                        </label>
                        {editing && (
                          <button
                            type="button"
                            onClick={planWeeks}
                            disabled={planning || !form.curriculum.length}
                            className="text-xs bg-[#0a788c] text-white px-3 py-1.5 hover:bg-[#0a96b4] disabled:opacity-50 flex items-center gap-1"
                            data-testid="plan-weeks-btn"
                          >
                            <Sparkles size={11} strokeWidth={1.5} />
                            {planning ? "KI plant…" : (form.weekly_plan.length ? "Neu generieren" : "Wochen-Plan generieren")}
                          </button>
                        )}
                      </div>
                      {!editing ? (
                        <div className="text-[10px] text-slate-500 italic">Erst nach dem ersten Speichern der Klasse verfügbar.</div>
                      ) : form.weekly_plan.length === 0 ? (
                        <div className="text-[10px] text-slate-500 italic">Klick auf &bdquo;Wochen-Plan generieren&ldquo; — die KI verteilt das Curriculum auf die Kalenderwochen.</div>
                      ) : (
                        <div>
                          <div className="text-[10px] text-slate-500 italic mb-2">
                            Themen und Unterthemen sind editierbar. Komma-getrennt eingeben, um mehrere Themen einer Woche zuzuordnen.
                          </div>
                          <div className="border border-slate-200 max-h-[420px] overflow-y-auto divide-y divide-slate-100">
                            {form.weekly_plan.map((w, idx) => (
                              <div key={w.week_number} className="p-3 space-y-2" data-testid={`week-${w.week_number}`}>
                                <div className="flex items-baseline justify-between gap-3">
                                  <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 shrink-0">
                                    Woche {w.week_number}
                                  </div>
                                  <div className="text-[10px] font-mono text-slate-500">{w.start_date} – {w.end_date}</div>
                                </div>
                                <input
                                  className="input-tech text-sm font-medium"
                                  placeholder="Wochen-Thema"
                                  value={w.theme || ""}
                                  onChange={(e) => setForm((f) => ({
                                    ...f,
                                    weekly_plan: f.weekly_plan.map((x, i) => (i === idx ? { ...x, theme: e.target.value } : x)),
                                  }))}
                                  data-testid={`week-theme-${w.week_number}`}
                                />
                                <input
                                  className="input-tech text-xs"
                                  placeholder="Themen (kommagetrennt)"
                                  value={(w.topics || []).join(", ")}
                                  onChange={(e) => setForm((f) => ({
                                    ...f,
                                    weekly_plan: f.weekly_plan.map((x, i) => (i === idx ? { ...x, topics: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } : x)),
                                  }))}
                                  data-testid={`week-topics-${w.week_number}`}
                                />
                                <input
                                  className="input-tech text-xs"
                                  placeholder="Unterthemen (kommagetrennt)"
                                  value={(w.subtopics || []).join(", ")}
                                  onChange={(e) => setForm((f) => ({
                                    ...f,
                                    weekly_plan: f.weekly_plan.map((x, i) => (i === idx ? { ...x, subtopics: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) } : x)),
                                  }))}
                                  data-testid={`week-subtopics-${w.week_number}`}
                                />
                                <div className="flex gap-1">
                                  {idx > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => setForm((f) => {
                                        const wp = [...f.weekly_plan];
                                        const cur = wp[idx];
                                        const prev = wp[idx - 1];
                                        // move all topics + subtopics from current to previous
                                        wp[idx - 1] = {
                                          ...prev,
                                          topics: [...(prev.topics || []), ...(cur.topics || [])],
                                          subtopics: [...(prev.subtopics || []), ...(cur.subtopics || [])],
                                        };
                                        wp[idx] = { ...cur, topics: [], subtopics: [] };
                                        return { ...f, weekly_plan: wp };
                                      })}
                                      className="text-[10px] text-slate-500 hover:text-slate-900 px-2 py-1 border border-slate-300 hover:bg-slate-50"
                                      title="Themen in vorherige Woche verschieben"
                                    >
                                      ↑ nach Woche {w.week_number - 1}
                                    </button>
                                  )}
                                  {idx < form.weekly_plan.length - 1 && (
                                    <button
                                      type="button"
                                      onClick={() => setForm((f) => {
                                        const wp = [...f.weekly_plan];
                                        const cur = wp[idx];
                                        const nxt = wp[idx + 1];
                                        wp[idx + 1] = {
                                          ...nxt,
                                          topics: [...(cur.topics || []), ...(nxt.topics || [])],
                                          subtopics: [...(cur.subtopics || []), ...(nxt.subtopics || [])],
                                        };
                                        wp[idx] = { ...cur, topics: [], subtopics: [] };
                                        return { ...f, weekly_plan: wp };
                                      })}
                                      className="text-[10px] text-slate-500 hover:text-slate-900 px-2 py-1 border border-slate-300 hover:bg-slate-50"
                                      title="Themen in nächste Woche verschieben"
                                    >
                                      ↓ nach Woche {w.week_number + 1}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="px-8 py-5 border-t border-slate-100">
          <button onClick={() => onOpenChange(false)} className="px-6 py-3 text-sm border border-slate-300 text-slate-700">Abbrechen</button>
          <button onClick={submit} disabled={saving} data-testid="class-save-btn" className="px-6 py-3 text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
            {saving ? "Speichere…" : editing ? "Speichern" : "Klasse anlegen"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
